/**
 * sessioneScaduta.js — la sessione scade, il testo scritto non si perde in
 * silenzio (29/10/2026, dal beta: scenario 9901).
 *
 * IL DIFETTO. Una 401 - per esempio di una lettura periodica degli avvisi del
 * Layout, col gettone scaduto - passava da services/api.js, che chiudeva lo
 * store (logout) PRIMA di mandare la pagina all'accesso. Con lo store gia' a
 * «non autenticato» la guardia delle schermate (App.js, Schermata) faceva
 * subito <Navigate to="/login"> dal router: niente beforeunload, niente
 * avviso, e una finestra aperta con del testo scritto spariva. Contraddiceva
 * la regola dei campi che si salvano da soli (CLAUDE.md): un campo scritto
 * non si perde mai in silenzio.
 *
 * ADESSO, a ogni 401 (qualunque rotta, non solo le letture periodiche):
 *   - SENZA lavoro a meta': come prima, la sessione si chiude tutta e si va
 *     all'accesso (esciAlLogin);
 *   - CON del lavoro a meta' - le STESSE guardie della ricarica automatica
 *     (utils/versioneInLinea.js, lavoroAMeta: un modulo sporco, un campo che
 *     si salva da solo scritto e non uscito, un campo trattenuto, un segreto
 *     a video), dichiarate qui da quel modulo e non copiate - la sessione
 *     NON si chiude: compare l'avviso (components/SessioneScaduta.jsx) e il
 *     testo resta a schermo. Si va all'accesso solo quando la persona
 *     conferma, con «Entra di nuovo»; o se la pagina se ne va da se'
 *     (chiusura, ricarica: passa dal beforeunload, che avvisa).
 *
 * LO STORE NON SI AZZERA PRIMA DELLA CONFERMA: e' proprio l'azzeramento a far
 * scattare il <Navigate> del router. Le 401 che arrivano dopo, con l'avviso a
 * video, non fanno niente: niente secondo avviso, niente navigazione, quindi
 * nessun giro.
 *
 * E IL CICLO DEL 15/10 NON TORNA (navigazione/ciclo401.test.js): «auth-storage»
 * si chiude sempre prima che la pagina d'accesso si carichi - da esciAlLogin,
 * oppure, se la pagina se ne va con l'avviso a video, al pagehide. Su /login
 * una 401 chiude la sessione e non ricarica, come prima.
 *
 * NIENTE IMPORT DI versioneInLinea: quel modulo importa services/api.js, che
 * importa questo. Le guardie arrivano con dichiaraLavoroAMeta(), chiamata da
 * versioneInLinea quando si carica (lo carica sempre App.js, tramite
 * uscitaProtetta). Senza dichiarazione non c'e' lavoro a meta': il
 * comportamento di prima.
 */
import { create } from 'zustand';
import { useAuthStore } from '../store/useStore';

export const MSG_SESSIONE_SCADUTA =
    'La sessione è scaduta: quello che hai scritto è ancora qui, ma non è salvato. Copialo se ti serve, poi entra di nuovo.';

export const useSessioneScaduta = create(() => ({ scaduta: false }));

let lavoroAMeta = () => null;

/** Chi sa dire se c'e' del lavoro a meta' (utils/versioneInLinea.js). */
export function dichiaraLavoroAMeta(fn) {
    if (typeof fn === 'function') lavoroAMeta = fn;
}

/* Tutta la sessione: il gettone, l'utente, e lo stato che lo store tiene in
   «auth-storage» (il ciclo del 15/10: vedi services/api.js). */
function chiudiSessione() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    useAuthStore.getState().logout();
}

/** All'accesso, senza lavoro a meta': la sessione chiusa, poi la pagina.
    Gia' sull'accesso non si ricarica: una 401 li' viene da una lettura
    rimasta indietro, e ricaricare non la correggerebbe. */
function esciAlLogin() {
    chiudiSessione();
    if (window.location.pathname !== '/login') window.location.href = '/login';
}

/* «Entra di nuovo»: la persona ha letto l'avviso e lascia la pagina. */
let confermata = false;
/** La persona ha confermato l'uscita dall'avviso: GuardiaUscita
    (utils/uscitaProtetta.js) non rifa' la domanda del browser. */
export const uscitaConfermata = () => confermata;

/**
 * Dall'avviso all'accesso. La sessione si chiude nella MEMORIA DEL BROWSER
 * (gettone, utente, «auth-storage»: la pagina d'accesso nasce gia' fuori, e il
 * ciclo del 15/10 non torna), ma NON nello store in memoria di questa pagina:
 * azzerarlo farebbe scattare il <Navigate> della guardia e, con un modulo
 * sporco, la domanda «Ci sono modifiche non salvate» del router, una seconda
 * volta dopo l'avviso. La pagina se ne va con la sua navigazione; al pagehide
 * si chiude anche lo store (chiudiAllUscita).
 */
export function entraDiNuovo() {
    confermata = true;
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    useAuthStore.persist.clearStorage();
    if (window.location.pathname !== '/login') window.location.href = '/login';
}

/* Con l'avviso a video la pagina se ne va da se' (chiusura, ricarica, un
   indirizzo scritto a mano): la sessione si chiude adesso, cosi' la pagina
   dopo nasce gia' fuori, senza un giro di 401. */
let ascolto = false;
function chiudiAllUscita() {
    if (ascolto) return;
    ascolto = true;
    window.addEventListener('pagehide', () => {
        if (useSessioneScaduta.getState().scaduta) chiudiSessione();
    });
}

/* Il gettone con cui e' partita una richiesta (l'intestazione che le ha messo
   services/api.js), o null. */
function gettoneDi(config) {
    const h = config && config.headers;
    if (!h) return null;
    const a = typeof h.get === 'function' ? h.get('Authorization') : h.Authorization;
    const m = typeof a === 'string' && a.match(/^Bearer (.+)$/);
    return m ? m[1] : null;
}

/* Il gettone che era in memoria quando e' comparso l'avviso. */
let gettoneDellAvviso = null;

/**
 * Una risposta riuscita, con l'avviso a video: se la richiesta portava un
 * gettone NUOVO - la persona e' entrata di nuovo da un'altra scheda, e le
 * schede condividono la memoria del browser - la sessione c'e' di nuovo e
 * l'avviso se ne va (il campo rimasto «non salvato» si puo' riprovare). Col
 * gettone di prima no: una richiesta partita prima della scadenza puo'
 * tornare buona dopo la 401 di un'altra.
 */
export function allaRispostaRiuscita(config) {
    if (!useSessioneScaduta.getState().scaduta) return;
    const g = gettoneDi(config);
    if (g && g !== gettoneDellAvviso) useSessioneScaduta.setState({ scaduta: false });
}

/** Una risposta 401: services/api.js la passa di qui, con la richiesta. */
export function allaRisposta401(config) {
    if (useSessioneScaduta.getState().scaduta) return;          // l'avviso c'e' gia'
    /* Una 401 di una richiesta partita col gettone di PRIMA, quando in memoria
       ce n'e' gia' un altro (entrati di nuovo da un'altra scheda): e' rimasta
       indietro, e la sessione di adesso non c'entra. Chiuderla toglierebbe il
       gettone buono anche all'altra scheda. */
    const partito = gettoneDi(config);
    const adesso = localStorage.getItem('token');
    if (partito && adesso && partito !== adesso) return;
    let aMeta = null;
    /* Una guardia che solleva non dice «niente a meta'»: nel dubbio l'avviso,
       che costa un clic, invece di un testo perso. */
    /* Il tablet resta com'era, di proposito (come per «vuoi uscire senza
       salvare», utils/uscitaProtetta.js): un avviso di sessione davanti al
       paziente e' un avviso che il paziente non sa interpretare. */
    const qui = window.location.pathname;
    const senzaAvviso = qui === '/login' || /^\/tablet(\/|$)/.test(qui);
    try { aMeta = senzaAvviso ? null : lavoroAMeta(); } catch (e) { aMeta = 'dubbio'; }
    if (aMeta) {
        chiudiAllUscita();
        gettoneDellAvviso = adesso;
        useSessioneScaduta.setState({ scaduta: true });
        return;
    }
    esciAlLogin();
}
